/**
 * The detail half of a profile, read two ways.
 *
 * By module answers "which dependency cost me this run" — the question a
 * cold start raises, and one a flat list buries across hundreds of import
 * frames. All functions keeps the flat, fully sortable view.
 *
 * Sorting and filtering are client-side because the payload is already whole
 * — the backend caps it at the top 200 by cumulative time, so there is no
 * second page to fetch and no server round trip to spend on a sort.
 *
 * `calls` arrives as text, not a number, because a recursive function reports
 * `total/primitive`. Sorting it therefore reads the total off the front
 * rather than comparing strings, which would put "9" after "10".
 */
import { useMemo, useState } from 'react';

import type { TaskProfileFunction } from '@optum/api-client/types';

import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Icon, Typography } from '../../ui/finixt.js';
import { callTotal, groupByModule, type ModuleGroup } from './profile-modules.js';

type SortKey = 'calls' | 'own_ms' | 'cumulative_ms';
type View = 'modules' | 'functions';

const COLUMNS: { key: SortKey; label: string; title: string; groupable: boolean }[] = [
  { key: 'calls', label: 'ncalls', title: 'Number of calls', groupable: true },
  { key: 'own_ms', label: 'tottime', title: 'Time in this function alone', groupable: true },
  {
    key: 'cumulative_ms',
    label: 'cumtime',
    title: 'Time including everything it called',
    groupable: false,
  },
];

const NOT_SUMMABLE =
  'Cumulative time counts a caller and everything it called, so it cannot be summed across a module.';

function sortValue(fn: TaskProfileFunction, key: SortKey): number {
  return key === 'calls' ? callTotal(fn.calls) : fn[key];
}

function matches(fn: TaskProfileFunction, needle: string): boolean {
  return fn.name.toLowerCase().includes(needle) || fn.file.toLowerCase().includes(needle);
}

export function ProfileFunctionTable({
  functions,
  wallMs,
}: {
  functions: TaskProfileFunction[];
  wallMs: number;
}) {
  const [view, setView] = useState<View>('modules');
  const [sort, setSort] = useState<SortKey>('cumulative_ms');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const needle = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (needle ? functions.filter((fn) => matches(fn, needle)) : functions),
    [functions, needle],
  );

  // A module sort has to fall back: cumulative time is not summable, so
  // choosing it in the flat view leaves the groups on their own default.
  const groupSort: 'calls' | 'own_ms' = sort === 'calls' ? 'calls' : 'own_ms';
  const groups = useMemo(() => {
    const built = groupByModule(filtered);
    return groupSort === 'calls' ? [...built].sort((a, b) => b.calls - a.calls) : built;
  }, [filtered, groupSort]);

  const rows = useMemo(
    () => [...filtered].sort((a, b) => sortValue(b, sort) - sortValue(a, sort)),
    [filtered, sort],
  );

  // While filtering, the operator is hunting a specific frame — leaving the
  // matches folded away would hide the thing they just searched for.
  const filtering = needle.length > 0;
  const allOpen = filtering || (groups.length > 0 && expanded.size === groups.length);

  function toggle(module: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(module)) next.add(module);
      return next;
    });
  }

  function toggleAll() {
    setExpanded(allOpen ? new Set() : new Set(groups.map((g) => g.module)));
  }

  const grouped = view === 'modules';
  const shown = grouped ? groups.length : rows.length;
  const total = grouped ? groupByModule(functions).length : functions.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 11,
        }}
      >
        <Typography variant="card">Top functions</Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <SegmentedControl
            items={[
              { value: 'modules', label: 'By module', icon: 'account_tree' },
              { value: 'functions', label: 'All functions', icon: 'list' },
            ]}
            value={view}
            onChange={(v) => setView(v as View)}
          />
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              borderRadius: 'var(--radius-control)',
              border: '1px solid var(--border-hairline)',
              background: 'var(--surface-card)',
              paddingLeft: 7,
              paddingRight: 7,
              paddingTop: 5,
              paddingBottom: 5,
            }}
          >
            <Icon name="search" size={14} style={{ color: 'var(--text-muted)' }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter functions…"
              aria-label="Filter functions"
              style={{
                width: 160,
                background: 'transparent',
                fontSize: 'var(--text-meta-lg)',
                fontFamily: 'inherit',
              }}
            />
          </label>
        </div>
      </div>

      <div
        style={{
          overflowX: 'auto',
          borderRadius: 'var(--radius-input)',
          border: '1px solid var(--border-hairline)',
        }}
      >
        <table style={{ width: '100%', fontSize: 'var(--text-meta-lg)' }}>
          <thead style={{ background: 'var(--surface-fill)' }}>
            <tr>
              <th
                scope="col"
                style={{
                  paddingLeft: 11,
                  paddingRight: 11,
                  paddingTop: 7,
                  paddingBottom: 7,
                  textAlign: 'left',
                  fontWeight: 'var(--weight-medium)',
                  color: 'var(--text-muted)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  {grouped && (
                    <button
                      type="button"
                      onClick={toggleAll}
                      disabled={filtering}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        fontFamily: 'inherit',
                      }}
                    >
                      <Icon name={allOpen ? 'unfold_less' : 'unfold_more'} size={14} />
                      {allOpen ? 'Collapse all' : 'Expand all'}
                    </button>
                  )}
                  {!grouped && 'Function'}
                </div>
              </th>
              {COLUMNS.map((col) => {
                const sortable = !grouped || col.groupable;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    style={{
                      paddingLeft: 11,
                      paddingRight: 11,
                      paddingTop: 7,
                      paddingBottom: 7,
                      textAlign: 'right',
                      fontWeight: 'var(--weight-medium)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        title={col.title}
                        onClick={() => setSort(col.key)}
                        aria-pressed={sort === col.key}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          fontFamily: 'inherit',
                        }}
                        className="mono"
                      >
                        {col.label}
                        {sort === col.key && <Icon name="arrow_downward" size={13} />}
                      </button>
                    ) : (
                      <span title={NOT_SUMMABLE} className="mono">
                        {col.label}
                      </span>
                    )}
                  </th>
                );
              })}
              <th
                scope="col"
                style={{
                  paddingLeft: 11,
                  paddingRight: 11,
                  paddingTop: 7,
                  paddingBottom: 7,
                  textAlign: 'right',
                  fontWeight: 'var(--weight-medium)',
                  color: 'var(--text-muted)',
                }}
              >
                % wall
              </th>
            </tr>
          </thead>
          <tbody>
            {grouped
              ? groups.map((group) => (
                  <ModuleRows
                    key={group.module}
                    group={group}
                    wallMs={wallMs}
                    open={filtering || expanded.has(group.module)}
                    onToggle={() => toggle(group.module)}
                  />
                ))
              : rows.map((fn) => (
                  <FunctionRow key={`${fn.file}:${fn.line}:${fn.name}`} fn={fn} wallMs={wallMs} />
                ))}
            {shown === 0 && (
              <tr
                style={{
                  borderTop: '1px solid var(--border-hairline)',
                  borderColor: 'var(--border-hairline)',
                }}
              >
                <td
                  colSpan={5}
                  style={{
                    paddingLeft: 11,
                    paddingRight: 11,
                    paddingTop: 22,
                    paddingBottom: 22,
                    textAlign: 'center',
                    color: 'var(--text-muted)',
                  }}
                >
                  No function matches that filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {/* The one place the overhead is disclosed. */}
        <div
          style={{
            borderTop: '1px solid var(--border-hairline)',
            borderColor: 'var(--border-hairline)',
            background: 'var(--surface-sunken)',
            paddingLeft: 11,
            paddingRight: 11,
            paddingTop: 7,
            paddingBottom: 7,
            fontSize: 'var(--text-meta-lg)',
            color: 'var(--text-muted)',
          }}
        >
          {shown} of {total} {grouped ? 'modules' : 'functions'} · times in ms ·{' '}
          {grouped && 'a module totals its own time, never its cumulative time · '}
          measuring a run makes it slower than an unmeasured one
        </div>
      </div>
    </div>
  );
}

function ModuleRows({
  group,
  wallMs,
  open,
  onToggle,
}: {
  group: ModuleGroup;
  wallMs: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        style={{
          borderTop: '1px solid var(--border-hairline)',
          borderColor: 'var(--border-hairline)',
          background: 'var(--surface-sunken)',
        }}
      >
        <td style={{ paddingLeft: 11, paddingRight: 11, paddingTop: 7, paddingBottom: 7 }}>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              textAlign: 'left',
              fontFamily: 'inherit',
            }}
          >
            <Icon name={open ? 'expand_more' : 'chevron_right'} size={16} />
            <span
              style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--text-strong)' }}
              className="mono"
            >
              {group.module}
            </span>
            <span style={{ color: 'var(--text-muted)' }}>
              {group.functions.length} fn
              {group.functions.length === 1 ? '' : 's'}
            </span>
          </button>
        </td>
        <td
          style={{
            paddingLeft: 11,
            paddingRight: 11,
            paddingTop: 7,
            paddingBottom: 7,
            textAlign: 'right',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--text-strong)',
          }}
          className="mono"
        >
          {group.calls.toLocaleString()}
        </td>
        <td
          style={{
            paddingLeft: 11,
            paddingRight: 11,
            paddingTop: 7,
            paddingBottom: 7,
            textAlign: 'right',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--text-strong)',
          }}
          className="mono"
        >
          {group.ownMs.toFixed(1)}
        </td>
        <td
          title={NOT_SUMMABLE}
          style={{
            paddingLeft: 11,
            paddingRight: 11,
            paddingTop: 7,
            paddingBottom: 7,
            textAlign: 'right',
            color: 'var(--text-muted)',
          }}
          className="mono"
        >
          —
        </td>
        <td
          style={{
            paddingLeft: 11,
            paddingRight: 11,
            paddingTop: 7,
            paddingBottom: 7,
            textAlign: 'right',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--text-muted)',
          }}
          className="mono"
        >
          {wallMs > 0 ? `${Math.round((group.ownMs / wallMs) * 100)}%` : '—'}
        </td>
      </tr>
      {open &&
        group.functions.map((fn) => (
          <FunctionRow key={`${fn.file}:${fn.line}:${fn.name}`} fn={fn} wallMs={wallMs} indent />
        ))}
    </>
  );
}

function FunctionRow({
  fn,
  wallMs,
  indent,
}: {
  fn: TaskProfileFunction;
  wallMs: number;
  indent?: boolean;
}) {
  return (
    <tr
      style={{
        borderTop: '1px solid var(--border-hairline)',
        borderColor: 'var(--border-hairline)',
        verticalAlign: 'top',
      }}
    >
      <td style={indent ? { padding: '7px 11px 7px 40px' } : { padding: '7px 11px' }}>
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'var(--text-strong)',
          }}
          className="mono"
        >
          {fn.name}
        </div>
        <div
          title={`${fn.file}:${fn.line}`}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 'var(--text-meta-lg)',
            color: 'var(--text-muted)',
          }}
          className="mono"
        >
          {fn.file}:{fn.line}
        </div>
      </td>
      <td
        style={{
          paddingLeft: 11,
          paddingRight: 11,
          paddingTop: 7,
          paddingBottom: 7,
          textAlign: 'right',
          color: 'var(--text-strong)',
        }}
        className="mono"
      >
        {fn.calls}
      </td>
      <td
        style={{
          paddingLeft: 11,
          paddingRight: 11,
          paddingTop: 7,
          paddingBottom: 7,
          textAlign: 'right',
          color: 'var(--text-strong)',
        }}
        className="mono"
      >
        {fn.own_ms.toFixed(1)}
      </td>
      <td
        style={{
          paddingLeft: 11,
          paddingRight: 11,
          paddingTop: 7,
          paddingBottom: 7,
          textAlign: 'right',
          color: 'var(--text-strong)',
        }}
        className="mono"
      >
        {fn.cumulative_ms.toFixed(1)}
      </td>
      <td
        style={{
          paddingLeft: 11,
          paddingRight: 11,
          paddingTop: 7,
          paddingBottom: 7,
          textAlign: 'right',
          color: 'var(--text-muted)',
        }}
        className="mono"
      >
        {wallMs > 0 ? `${Math.round((fn.cumulative_ms / wallMs) * 100)}%` : '—'}
      </td>
    </tr>
  );
}
